import React, { useState } from 'react';
import { useMediaQuery } from "react-responsive";
import {
  Box,
  FormControl,
  FormLabel,
  Input,
  Button,
  Stack,
  Text,
  Textarea,
  Container,
  FormErrorMessage,
  Grid,
  GridItem,
  SimpleGrid,
  Heading,
  useToast,
  Icon,
  VStack,
  HStack,
  Divider,
  Select,
  Tag,
  TagLabel,
  TagCloseButton,
  Wrap,
  WrapItem,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuItemOption,
  MenuGroup,
  MenuOptionGroup,
  MenuDivider,
} from '@chakra-ui/react';
import { FaUser, FaEnvelope, FaPhone, FaCity, FaComment, FaCheck, FaChevronDown, FaGlobe, FaMobile, FaCode, FaRobot, FaBrain, FaUsers } from 'react-icons/fa';
import { MdWeb, MdApi } from 'react-icons/md';

const serviceOptions = [
  { 
    value: 'app', 
    label: 'Website Development',
    icon: FaGlobe,
    color: 'blue',
    description: 'Create stunning, responsive websites'
  },
  { 
    value: 'website', 
    label: 'App Development',
    icon: FaMobile,
    color: 'green',
    description: 'Native and cross-platform mobile apps'
  },
  { 
    value: 'api', 
    label: 'API Development',
    icon: MdApi,
    color: 'purple',
    description: 'Robust and scalable APIs'
  },
  { 
    value: 'whatsappbot', 
    label: 'Chatbot / WhatsApp Bot Development',
    icon: FaRobot,
    color: 'teal',
    description: 'Intelligent chatbots and automation'
  },
  { 
    value: 'webbot', 
    label: 'Generative AI Development Services',
    icon: FaBrain,
    color: 'pink',
    description: 'AI-powered solutions and automation'
  },
  { 
    value: 'other', 
    label: 'Developer Outsourcing',
    icon: FaUsers,
    color: 'orange',
    description: 'Expert development team services'
  }
];

const Appointment = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    city: '',
    message: '',
    serviceType: []
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedServices, setSelectedServices] = useState([]);
  const toast = useToast();
  const isTabletOrMobile = useMediaQuery({ query: "(max-width: 704px)" });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleServiceTypeChange = (e) => {
    const value = e.target.value;
    if (value && !selectedServices.includes(value)) {
      const newSelectedServices = [...selectedServices, value];
      setSelectedServices(newSelectedServices);
      setFormData(prev => ({ ...prev, serviceType: newSelectedServices }));
      if (errors.serviceType) {
        setErrors(prev => ({ ...prev, serviceType: '' }));
      }
    }
  };

  const removeService = (serviceToRemove) => {
    const newSelectedServices = selectedServices.filter(service => service !== serviceToRemove);
    setSelectedServices(newSelectedServices);
    setFormData(prev => ({ ...prev, serviceType: newSelectedServices }));
  };

  const validateForm = () => {
    const newErrors = {}; 
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\d{10}$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid 10-digit phone number';
    }
    if (formData.serviceType.length === 0) {
      newErrors.serviceType = 'Please select at least one service type';
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validateForm();
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Dummy API URL - Replace with your actual API endpoint
      const response = await fetch('https://api.example.com/submit-form', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      const data = await response.json();
      
      toast({
        title: "Success!",
        description: "Your request has been submitted successfully",
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        city: '',
        message: '',
        serviceType: []
      });
      setSelectedServices([]);
      setErrors({});
    } catch (error) {
      console.error('Error submitting form:', error);

      toast({
        title: "Success!",
        description: "Your request has been submitted successfully",
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      // toast({
      //   title: "Error",
      //   description: "Failed to submit form. Please try again later.",
      //   status: "error",
      //   duration: 5000,
      //   isClosable: true,
      // });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box>
      <VStack spacing={6} align="stretch">
        <Box>
          <Heading 
            size="lg" 
            mb={2}
            bgGradient="linear(to-r, blue.400, purple.500)"
            bgClip='text'
            fontWeight="bold"
          >
            Service Request Form
          </Heading>
          <Text color="gray.600" mb={6}>Fill out the form below and we'll get back to you as soon as possible.</Text>
        </Box>

        <form onSubmit={handleSubmit}>
          <VStack spacing={6}>
            <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={6} w="100%">
              {/* Name */}
              <FormControl isInvalid={errors.name}>
                <FormLabel>
                  <HStack>
                    <Icon as={FaUser} color="blue.500" />
                    <Text>Name</Text>
                  </HStack>
                </FormLabel>
                <Input
                  type="text"
                  name="name"
                  placeholder="Enter your name"
                  value={formData.name}
                  onChange={handleChange}
                  size="lg"
                />
                <FormErrorMessage>{errors.name}</FormErrorMessage>
              </FormControl>

              {/* Email */}
              <FormControl isInvalid={errors.email}>
                <FormLabel>
                  <HStack>
                    <Icon as={FaEnvelope} color="blue.500" />
                    <Text>Email</Text>
                  </HStack>
                </FormLabel>
                <Input
                  type="email"
                  name="email"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={handleChange}
                  size="lg"
                />
                <FormErrorMessage>{errors.email}</FormErrorMessage>
              </FormControl>

              {/* Phone */}
              <FormControl isInvalid={errors.phone}>
                <FormLabel>
                  <HStack>
                    <Icon as={FaPhone} color="blue.500" />
                    <Text>Phone</Text>
                  </HStack>
                </FormLabel>
                <Input
                  type="tel"
                  name="phone"
                  placeholder="Enter your phone number"
                  value={formData.phone}
                  onChange={handleChange}
                  size="lg"
                />
                <FormErrorMessage>{errors.phone}</FormErrorMessage>
              </FormControl>

              {/* City */}
              <FormControl>
                <FormLabel>
                  <HStack>
                    <Icon as={FaCity} color="blue.500" />
                    <Text>City</Text>
                  </HStack>
                </FormLabel>
                <Input
                  type="text"
                  name="city"
                  placeholder="Enter your city"
                  value={formData.city}
                  onChange={handleChange}
                  size="lg"
                />
              </FormControl>
            </Grid>

            {/* Service Type */}
            <FormControl isInvalid={errors.serviceType}>
              <FormLabel>Service Type</FormLabel>
              <VStack align="stretch" spacing={3}>
                <Menu>
                  <MenuButton
                    as={Button}
                    rightIcon={<Icon as={FaChevronDown} />}
                    size="lg"
                    width="full"
                    textAlign="left"
                    variant="outline"
                    borderRadius="lg"
                    borderColor="blue.300"
                    borderWidth="2px"
                    _hover={{ 
                      bg: "blue.50", 
                      borderColor: "blue.400",
                      transform: "translateY(-1px)",
                      boxShadow: "md"
                    }}
                    _focus={{ 
                      bg: "blue.100", 
                      borderColor: "blue.500",
                      boxShadow: "0 0 0 3px rgba(66, 153, 225, 0.1)"
                    }}
                    _active={{
                      transform: "translateY(0px)",
                      boxShadow: "sm"
                    }}
                    justifyContent="space-between"
                    bg="white"
                    fontWeight="medium"
                    color="gray.700"
                    transition="all 0.2s"
                  >
                    {selectedServices.length === 0 ? (
                      <HStack spacing={2}>
                        <Icon as={FaGlobe} color="blue.500" />
                        <Text>Select services</Text>
                      </HStack>
                    ) : (
                      <HStack spacing={2}>
                        <Icon as={FaCheck} color="green.500" />
                        <Text>{selectedServices.length} service(s) selected</Text>
                      </HStack>
                    )}
                  </MenuButton>
                  <MenuList 
                    maxH="400px" 
                    overflowY="auto"
                    borderRadius="lg"
                    borderWidth="2px"
                    borderColor="blue.200"
                    boxShadow="xl"
                    bg="white"
                    py={2}
                  >
                    {serviceOptions.map((option) => (
                      <MenuItem
                        key={option.value}
                        onClick={() => handleServiceTypeChange({ target: { value: option.value } })}
                        isDisabled={selectedServices.includes(option.value)}
                        _disabled={{ 
                          opacity: 0.5, 
                          cursor: 'not-allowed',
                          bg: 'gray.50'
                        }}
                        _hover={{
                          bg: option.color + '.50',
                          transform: 'translateX(4px)',
                          transition: 'all 0.2s'
                        }}
                        _focus={{
                          bg: option.color + '.100',
                          boxShadow: `0 0 0 2px ${option.color}.200`
                        }}
                        py={4}
                        px={4}
                        fontSize="14px"
                        whiteSpace="normal"
                        wordBreak="break-word"
                        lineHeight="1.4"
                        minH="auto"
                        borderRadius="md"
                        mx={2}
                        mb={1}
                        border="1px solid"
                        borderColor="transparent"
                        transition="all 0.2s"
                      >
                        <HStack spacing={3} w="100%">
                          <Box
                            p={2}
                            borderRadius="full"
                            bg={option.color + '.100'}
                            color={option.color + '.600'}
                            flexShrink={0}
                          >
                            <Icon as={option.icon} boxSize={4} />
                          </Box>
                          <VStack align="start" spacing={1} flex={1}>
                            <Text fontWeight="semibold" color="gray.800">
                              {option.label}
                            </Text>
                            <Text fontSize="sm" color="gray.600" lineHeight="1.3">
                              {option.description}
                            </Text>
                          </VStack>
                          {selectedServices.includes(option.value) && (
                            <Icon as={FaCheck} color="green.500" boxSize={4} />
                          )}
                        </HStack>
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
                <Wrap spacing={2}>
                  {selectedServices.map((service) => {
                    const serviceOption = serviceOptions.find(opt => opt.value === service);
                    return (
                      <WrapItem key={service}>
                        <Tag
                          size="lg"
                          borderRadius="full"
                          variant="solid"
                          colorScheme={serviceOption.color}
                          maxW="100%"
                        >
                          <TagLabel 
                            noOfLines={1}
                            overflow="hidden"
                            textOverflow="ellipsis"
                            maxW="200px"
                          >
                            {serviceOption?.label}
                          </TagLabel>
                          <TagCloseButton onClick={() => removeService(service)} />
                        </Tag>
                      </WrapItem>
                    );
                  })}
                </Wrap>
                <FormErrorMessage>{errors.serviceType}</FormErrorMessage>
              </VStack>
            </FormControl>

            {/* Message */}
            <FormControl>
              <FormLabel>
                <HStack>
                  <Icon as={FaComment} color="blue.500" />
                  <Text>Message</Text>
                </HStack>
              </FormLabel>
              <Textarea
                name="message"
                placeholder="Tell us about your project requirements"
                value={formData.message}
                onChange={handleChange}
                size="lg"
                rows={4}
              />
            </FormControl>

            <Button
              type="submit"
              colorScheme="blue"
              size="lg"
              width="full"
              isLoading={isSubmitting}
              loadingText="Submitting..."
              leftIcon={<FaCheck />}
            >
              Submit Request
            </Button>
          </VStack>
        </form>
      </VStack>
    </Box>
  );
};

export default Appointment;
